import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Badge } from '@/components/ui/badge'
import apiClient from '@/api/client'
import { formatCurrency, formatDateTimeHuman } from '@/lib/utils'
import { OrderReceiptCard } from '@/components/admin/OrderReceiptCard'
import type { CustomerOrderHistoryItem } from '@/types'

/**
 * The receipt behind one row of a customer's order history (F-FE-N141).
 *
 * The customer profile's order table answers "when and how much"; this answers
 * "what did they actually have", which is the question that makes a guest
 * profile worth opening. The row payload is deliberately lean — the history
 * endpoint sends an item *count*, not the lines — so the lines come from
 * `GET /orders/:id` on open.
 *
 * Read-only on purpose. The reports orders browser has a much heavier drawer
 * around the same receipt (guest-receipt reprint, PRA tax invoice, FBR fiscal
 * state, credit-note issuing); those actions POST to `/admin/orders/...`, which
 * is admin-only, and managers can open this profile. Rather than gate half a
 * panel per role, this surface carries none of it.
 */
export function CustomerOrderDrawer({
  order,
  onClose,
}: {
  order: CustomerOrderHistoryItem | null
  onClose: () => void
}) {
  // Hold the last opened row so the slide-out animation has something to
  // render while Radix tears the panel down — otherwise it flashes empty for
  // ~200ms on every close. Same trick as the reports drawer.
  const [lastOrder, setLastOrder] = useState<CustomerOrderHistoryItem | null>(order)
  useEffect(() => {
    if (order) setLastOrder(order)
  }, [order])
  const shown = order ?? lastOrder

  // Same cache key as the reports order drawer on purpose: both are read-only
  // views of the same document, so opening one pre-warms the other.
  const detailQuery = useQuery({
    queryKey: ['order-detail', shown?.id],
    queryFn: () => apiClient.getOrder(shown!.id),
    enabled: !!order && !!shown?.id,
    staleTime: 60_000,
  })
  const fullOrder =
    detailQuery.data?.success && detailQuery.data.data ? detailQuery.data.data : null

  return (
    <Sheet
      open={!!order}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <SheetContent size="lg" className="p-0" aria-describedby={undefined}>
        {shown && (
          <>
            <SheetHeader>
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Order receipt
              </p>
              <SheetTitle className="tabular-nums">{shown.order_number}</SheetTitle>
              <p className="text-xs text-muted-foreground">
                Placed {formatDateTimeHuman(shown.created_at)}
              </p>
            </SheetHeader>
            <SheetBody className="space-y-4">
              {/* Every row here comes from the list payload we already hold, so
                  the panel is fully readable before the items arrive. */}
              <Row label="Status">
                <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                  {shown.cancelled_by_credit_note ? 'Credited' : shown.status}
                </Badge>
              </Row>
              <Row label="Type">
                <span className="capitalize">{shown.order_type.replace(/_/g, ' ')}</span>
              </Row>
              {shown.completed_at && (
                <Row label="Completed at">
                  {formatDateTimeHuman(shown.completed_at)}
                </Row>
              )}
              <Row label="Paid by">
                <span className="capitalize">{shown.payment_method ?? '—'}</span>
              </Row>
              <Row label="Items">
                <span className="tabular-nums">{shown.item_count}</span>
              </Row>
              <Row label="Discount">
                <span className="tabular-nums">
                  {shown.discount_amount > 0 ? `−${formatCurrency(shown.discount_amount)}` : '—'}
                </span>
              </Row>
              <Row label="Total">
                <span className="font-semibold tabular-nums">
                  {formatCurrency(shown.total_amount)}
                </span>
              </Row>

              <OrderReceiptCard
                creditNote={shown}
                fullOrder={fullOrder}
                isLoading={detailQuery.isLoading}
                isError={detailQuery.isError}
                onRetry={() => detailQuery.refetch()}
                formatCurrency={formatCurrency}
                emptyFallback={
                  <div className="rounded-md border p-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Receipt
                    </p>
                    <p className="pt-1 text-xs text-muted-foreground">
                      No items were recorded on this order.
                    </p>
                  </div>
                }
              />
            </SheetBody>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

/** Sentence-case summary row, matching the customer profile this drawer opens
 *  from. Intentionally not the shared `DetailRow`: that one carries the
 *  reports drawer's tracked-caps labels, and re-styling it would move twenty
 *  call sites on a screen nobody asked to change. */
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{children}</span>
    </div>
  )
}
